// HealthRangeBar — an honest "where you sit vs. healthy" gauge. Given a value and
// a healthy band (either bound may be open-ended), it draws a track with the
// healthy zone shaded green and a marker at the value, colored by assessment. It
// always renders as long as one bound is known — so the Home card and the Explain
// modal never fall back to an apology about missing history. Inline SVG, no libs.

import type { RatioAssessment } from '../../../../data/ServerApiClient'

export type HealthRangeBarProps = {
  value: number
  healthyMin: number | null
  healthyMax: number | null
  /** Pre-formatted value label, e.g. "-$882.3K" / "232.5%" / "0.44x". */
  display?: string | null
  /** Pre-formatted bound labels, e.g. "$0", "60%", "1.5x". */
  minLabel?: string | null
  maxLabel?: string | null
  assessment?: RatioAssessment | null
  /** Compact = the small inline bar on a Home card; full = the modal gauge. */
  size?: 'compact' | 'full'
  ariaLabel?: string
  className?: string
}

const finite = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v)

const toneColor = (assessment: RatioAssessment | null | undefined): string => {
  switch (assessment) {
    case 'concern':
      return 'var(--neg, #c0492f)'
    case 'watch':
      return 'var(--watch-acc, #b6791f)'
    case 'healthy':
      return 'var(--good-acc, #0e7c86)'
    default:
      return 'var(--muted, #6b7178)'
  }
}

/** Compute the plotted domain [lo, hi] that comfortably shows value + band. */
export function computeHealthDomain(
  value: number,
  healthyMin: number | null,
  healthyMax: number | null,
): { lo: number; hi: number } {
  const marks = [value, healthyMin, healthyMax].filter(finite)
  // Anchor an open one-sided band to zero so the shaded zone reads correctly.
  if (finite(healthyMin) && !finite(healthyMax)) marks.push(healthyMin + Math.max(Math.abs(healthyMin), Math.abs(value), 1) * 0.4)
  if (finite(healthyMax) && !finite(healthyMin)) marks.push(Math.min(0, healthyMax - Math.abs(healthyMax) * 0.4))
  let lo = Math.min(...marks)
  let hi = Math.max(...marks)
  if (lo === hi) {
    const pad = Math.abs(lo) || 1
    lo -= pad
    hi += pad
  }
  const span = hi - lo || 1
  return { lo: lo - span * 0.1, hi: hi + span * 0.1 }
}

export function HealthRangeBar({
  value,
  healthyMin,
  healthyMax,
  display,
  minLabel,
  maxLabel,
  assessment,
  size = 'compact',
  ariaLabel,
  className,
}: HealthRangeBarProps) {
  if (!finite(value) || (!finite(healthyMin) && !finite(healthyMax))) return null
  const full = size === 'full'
  const width = full ? 460 : 240
  const height = full ? 72 : 44
  const padX = 10
  const trackY = full ? 30 : 20
  const trackH = full ? 12 : 8
  const plotW = width - padX * 2

  const { lo, hi } = computeHealthDomain(value, healthyMin, healthyMax)
  const span = hi - lo || 1
  const x = (v: number) => padX + ((v - lo) / span) * plotW
  const clampX = (v: number) => Math.max(padX, Math.min(padX + plotW, x(v)))

  const zoneStart = finite(healthyMin) ? clampX(healthyMin) : padX
  const zoneEnd = finite(healthyMax) ? clampX(healthyMax) : padX + plotW
  const markerX = clampX(value)
  const color = toneColor(assessment)
  const label = ariaLabel || `Value ${display ?? value} against its healthy range`

  const valueLabelAnchor = markerX > width - 60 ? 'end' : markerX < 60 ? 'start' : 'middle'

  return (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: 'auto', display: 'block' }}
    >
      {/* base track */}
      <rect x={padX} y={trackY} width={plotW} height={trackH} rx={trackH / 2} fill="var(--health-track, #dce5e7)" />
      {/* healthy zone */}
      <rect
        x={zoneStart}
        y={trackY}
        width={Math.max(2, zoneEnd - zoneStart)}
        height={trackH}
        rx={trackH / 2}
        fill="var(--good-acc, #0e7c86)"
        opacity={0.34}
      />
      <rect
        x={zoneStart}
        y={trackY}
        width={Math.max(2, zoneEnd - zoneStart)}
        height={trackH}
        rx={trackH / 2}
        fill="none"
        stroke="var(--good-acc, #0e7c86)"
        strokeOpacity={0.9}
        strokeWidth={1.25}
      />
      {/* marker */}
      <line x1={markerX} y1={trackY - (full ? 8 : 5)} x2={markerX} y2={trackY + trackH + (full ? 8 : 5)} stroke={color} strokeWidth={full ? 3 : 2.5} strokeLinecap="round" />
      <circle cx={markerX} cy={trackY + trackH / 2} r={full ? 5.5 : 4.5} fill={color} stroke="var(--surface, #fff)" strokeWidth={1.5} />
      {/* value label */}
      {display ? (
        <text
          x={Math.max(padX, Math.min(padX + plotW, markerX))}
          y={trackY - (full ? 12 : 9)}
          textAnchor={valueLabelAnchor}
          style={{ font: `700 ${full ? 13 : 11}px var(--font-ui, system-ui)`, fill: color }}
        >
          {display}
        </text>
      ) : null}
      {/* healthy zone edge labels */}
      {full ? (
        <g style={{ font: '500 11px var(--font-ui, system-ui)', fill: 'var(--muted, #6b7178)' }}>
          {finite(healthyMin) && minLabel ? (
            <text x={zoneStart} y={trackY + trackH + 20} textAnchor="middle">{minLabel}</text>
          ) : null}
          {finite(healthyMax) && maxLabel ? (
            <text x={zoneEnd} y={trackY + trackH + 20} textAnchor="middle">{maxLabel}</text>
          ) : null}
          <text x={(zoneStart + zoneEnd) / 2} y={trackY - 12} textAnchor="middle" style={{ fill: 'var(--good-acc, #0e7c86)', font: '600 10px var(--font-ui, system-ui)' }}>
            healthy
          </text>
        </g>
      ) : null}
    </svg>
  )
}

export default HealthRangeBar
